import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  BottomDrawer,
  FormField,
  Alert,
  Badge,
  Paper,
  Text,
  Switch,
  Select,
  NumberInput,
} from '@vipso/shared';
import {
  useListApprovalFlowDefinitionsQuery,
  useUpsertApprovalFlowDefinitionMutation,
  useListRolesQuery,
} from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';

type LevelDraft = {
  key: string;
  roleId: string;
  minAmount: number;
};

type DefinitionRow = {
  id: string;
  moduleKey: string;
  isEnabled: boolean;
  levels?: Array<{
    id: string;
    levelOrder: number;
    roleId: string;
    roleName?: string | null;
    minAmount: number;
  }> | null;
};

const AMOUNT_MODULES = new Set(['PRODUCT', 'PROC_SALES', 'PROC_PURCHASE']);

const MODULE_ORDER = [
  'CUSTOMER',
  'VENDOR',
  'PRODUCT',
  'WAREHOUSE',
  'PROC_SALES',
  'PROC_PURCHASE',
] as const;

function moduleLabelKey(moduleKey: string): string {
  switch (moduleKey) {
    case 'CUSTOMER':
      return 'approvalFlows.modules.customer';
    case 'VENDOR':
      return 'approvalFlows.modules.vendor';
    case 'PRODUCT':
      return 'approvalFlows.modules.product';
    case 'WAREHOUSE':
      return 'approvalFlows.modules.warehouse';
    case 'PROC_SALES':
      return 'approvalFlows.modules.sales';
    case 'PROC_PURCHASE':
      return 'approvalFlows.modules.purchase';
    default:
      return moduleKey;
  }
}

export function ApprovalFlowsPage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();

  const { data, isLoading, isError, refetch } = useListApprovalFlowDefinitionsQuery();
  const { data: rolesData } = useListRolesQuery();
  const [upsert, { isLoading: saving }] = useUpsertApprovalFlowDefinitionMutation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DefinitionRow | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [levels, setLevels] = useState<LevelDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const roles = rolesData?.listRoles ?? [];
  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: String(r.id), label: r.name })),
    [roles],
  );

  const rows = useMemo(() => {
    const list = data?.listApprovalFlowDefinitions ?? [];
    const byKey = new Map(list.map((d) => [d.moduleKey, d]));
    return MODULE_ORDER.map((key) => byKey.get(key)).filter(Boolean) as DefinitionRow[];
  }, [data?.listApprovalFlowDefinitions]);

  function openEdit(row: DefinitionRow) {
    setEditing(row);
    setEnabled(row.isEnabled);
    setLevels(
      (row.levels ?? [])
        .slice()
        .sort((a, b) => a.levelOrder - b.levelOrder)
        .map((l, idx) => ({
          key: l.id || `l-${idx}`,
          roleId: String(l.roleId),
          minAmount: Number(l.minAmount ?? 0),
        })),
    );
    setError(null);
    setDrawerOpen(true);
  }

  function addLevel() {
    setLevels((prev) => [
      ...prev,
      { key: `new-${Date.now()}`, roleId: '', minAmount: 0 },
    ]);
  }

  function removeLevel(key: string) {
    setLevels((prev) => prev.filter((l) => l.key !== key));
  }

  function moveLevel(index: number, dir: -1 | 1) {
    setLevels((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[index]!;
      next[index] = next[j]!;
      next[j] = tmp;
      return next;
    });
  }

  async function handleSave() {
    if (!editing) return;
    setError(null);
    if (enabled && levels.length === 0) {
      setError(t('approvalFlows.validation.needLevel'));
      return;
    }
    if (enabled && levels.some((l) => !l.roleId)) {
      setError(t('approvalFlows.validation.needRole'));
      return;
    }
    try {
      await upsert({
        moduleKey: editing.moduleKey,
        isEnabled: enabled,
        levels: levels.map((l, idx) => ({
          levelOrder: idx + 1,
          roleId: l.roleId,
          minAmount: AMOUNT_MODULES.has(editing.moduleKey) ? Number(l.minAmount) || 0 : 0,
        })),
      }).unwrap();
      setDrawerOpen(false);
      await refetch();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message ?? '')
            : '';
      setError(message || t('approvalFlows.saveFailed'));
    }
  }

  const showAmount = editing ? AMOUNT_MODULES.has(editing.moduleKey) : false;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('approvalFlows.title')}
        description={t('approvalFlows.description')}
      />

      {isError ? <Alert color="red" title={t('approvalFlows.loadFailed')} /> : null}

      <Stack gap="md">
        {isLoading && !rows.length ? (
          <Text c="dimmed">{t('common.loading')}</Text>
        ) : (
          rows.map((row) => {
            const levelCount = row.levels?.length ?? 0;
            const amountBased = (row.levels ?? []).some((l) => Number(l.minAmount) > 0);
            const finalRole =
              levelCount > 0
                ? [...(row.levels ?? [])].sort((a, b) => a.levelOrder - b.levelOrder).at(-1)
                    ?.roleName
                : null;
            return (
              <Paper key={row.id} p="lg" withBorder radius="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <Stack gap={4}>
                    <Group gap="sm">
                      <Text fw={600}>{t(moduleLabelKey(row.moduleKey))}</Text>
                      <Badge
                        size="sm"
                        color={row.isEnabled ? 'green' : 'gray'}
                        variant="light"
                      >
                        {row.isEnabled
                          ? t('approvalFlows.enabled')
                          : t('approvalFlows.disabled')}
                      </Badge>
                      {amountBased ? (
                        <Badge size="sm" color="blue" variant="light">
                          {t('approvalFlows.amountBased')}
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {levelCount === 0
                        ? t('approvalFlows.noLevels')
                        : t('approvalFlows.levelSummary', {
                            count: levelCount,
                            final: finalRole || '—',
                          })}
                    </Text>
                  </Stack>
                  {canMutate() ? (
                    <Button variant="light" onClick={() => openEdit(row)}>
                      {t('common.edit')}
                    </Button>
                  ) : null}
                </Group>
              </Paper>
            );
          })
        )}
      </Stack>

      <BottomDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={
          editing
            ? t('approvalFlows.editTitle', { module: t(moduleLabelKey(editing.moduleKey)) })
            : t('approvalFlows.title')
        }
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('approvalFlows.autoApplyHint')}
          </Text>
          <FormField label={t('approvalFlows.enable')}>
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
              label={enabled ? t('approvalFlows.enabled') : t('approvalFlows.disabled')}
            />
          </FormField>

          <Group justify="space-between" align="center">
            <Text fw={600}>{t('approvalFlows.levels')}</Text>
            <Button size="xs" variant="light" onClick={addLevel}>
              {t('approvalFlows.addLevel')}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {t('approvalFlows.levelsHint')}
          </Text>

          {levels.map((level, index) => {
            const isFinal = index === levels.length - 1;
            return (
              <Paper key={level.key} p="md" withBorder radius="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>
                      {t('approvalFlows.levelN', { n: index + 1 })}
                      {isFinal ? ` · ${t('approvalFlows.finalApproval')}` : ''}
                    </Text>
                    <Group gap={4}>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={index === 0}
                        onClick={() => moveLevel(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={index === levels.length - 1}
                        onClick={() => moveLevel(index, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="subtle"
                        onClick={() => removeLevel(level.key)}
                      >
                        {t('common.delete')}
                      </Button>
                    </Group>
                  </Group>
                  <FormField label={t('approvalFlows.role')}>
                    <Select
                      data={roleOptions}
                      value={level.roleId || null}
                      onChange={(v) =>
                        setLevels((prev) =>
                          prev.map((l) =>
                            l.key === level.key ? { ...l, roleId: v || '' } : l,
                          ),
                        )
                      }
                      placeholder={t('approvalFlows.selectRole')}
                      searchable
                    />
                  </FormField>
                  {showAmount ? (
                    <FormField label={t('approvalFlows.minAmount')}>
                      <NumberInput
                        value={level.minAmount}
                        min={0}
                        onChange={(v) =>
                          setLevels((prev) =>
                            prev.map((l) =>
                              l.key === level.key
                                ? { ...l, minAmount: typeof v === 'number' ? v : 0 }
                                : l,
                            ),
                          )
                        }
                      />
                    </FormField>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}

          {error ? <Alert color="red" title={error} /> : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDrawerOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={saving} onClick={() => void handleSave()}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
